import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import './SupplierNormalization.scss';
import {Button, Chip, Typography} from "@mui/material";
import {useLocation, useNavigate, useSearchParams} from "react-router";
import {useStores} from "../../stores";
import {SupplierNormalizationWelcomeStep} from "./pages/SupplierNormalizationWelcomeStep";
import {SupplierNormalizationApplyStep} from "./pages/SupplierNormalizationApplyStep";
import {SupplierNormalizationModelRunningStep} from "./pages/SupplierNormalizationModelRunningStep";
import {SupplierNormalizationApplyingResultsStep} from "./pages/SupplierNormalizationApplyingResultsStep";
import {ParentSuppliersStep} from "./pages/parent-suppliers/ParentSuppliersStep";
import {generateHomePath} from "../../routing/routing.utils";
import {SupplierNormalizationState} from "./pages/SupplierNormalization.classes";
import {ChevronRight} from "@mui/icons-material";
import * as SupplierNormalizationUrls from "./SupplierNormalizationUrls";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import SupplierNormalizationPSHelp from "./SupplierNormalizationPSHelp.tsx";
import SupplierNormalizationSSHelp from "./SupplierNormalizationSSHelp.tsx";
import {SupplierNormalizationStepper} from "./SupplierNormalizationStepper.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import {environment} from "../../env.ts";
import {SupplierNormalizationWorkflows} from "./SupplierNormalizationWorkflows";

export const SupplierNormalizationPage: React.FC = observer(() => {
    const {
        p, rootAppStore, bagStore,
        cmpStore,
        supplierNormalizationStore,
        masterSupplierNormalizationStore,
        authStore,
    } = useStores();
    const bagId = bagStore.bagId;
    const navigate = useNavigate();
    const location = useLocation();
    const state = supplierNormalizationStore.state;

    // const isMaster = env === 'nestle' && location.pathname === routes.merge_1_supplier_normalization_master;
    const isMaster = false;

    const numberOfSuppliers = isMaster
        ? cmpStore.masterKpi?.n_suppliers
        : supplierNormalizationStore.statistics?.total_suppliers;

    /**
     * CAT-969
     * When threshold is lower than hideSmallStandaloneSpend, do not show standalone KPI's
     */
    const hideStandaloneOverview = p.p.hideSmallStandaloneSpend ? supplierNormalizationStore.statistics && (
        supplierNormalizationStore.statistics.standalone_suppliers <= p.p.hideSmallStandaloneSpend.minStandaloneSuppliers
    ) : false;

    const [searchParams] = useSearchParams();
    const locationStateSearchIntent = (location.state as SupplierNormalizationUrls.UrlState)?.searchIntent ?? null;
    useEffect(() => {
        let intent = locationStateSearchIntent;
        if (!intent) {
            intent = SupplierNormalizationUrls.urlToIntent(searchParams);
        }
        if (supplierNormalizationStore.isMaster) {
            if (!p.p.hardcodeMasterBagId) throw new Error('Hardcode master bag id is not set (ERROR FROM PAGE!!)');
            // Note: We use the MASTER store here
            masterSupplierNormalizationStore.init(p.p.hardcodeMasterBagId)
        } else {
            supplierNormalizationStore.init(bagId, true, intent)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let goBack = () => navigate(generateHomePath(location, {id: String(bagId)}, rootAppStore.app));
    let backLabel = 'Home';
    if (state === SupplierNormalizationState.START || state === SupplierNormalizationState.RUNNING) {
        // Go Home
    } else if (state === SupplierNormalizationState.REVIEW) {
        goBack = () => supplierNormalizationStore.goToState(SupplierNormalizationState.START);
        backLabel = 'Back';
    } else if (state === SupplierNormalizationState.APPLYING || state === SupplierNormalizationState.DONE) {
        goBack = () => supplierNormalizationStore.goToState(SupplierNormalizationState.REVIEW);
        backLabel = 'Back';
    }

    let nextNode = <></>
    if (state === SupplierNormalizationState.REVIEW && environment.isDemo) {
        nextNode = <Button variant="outlined"
                           onClick={() => supplierNormalizationStore.goToState(SupplierNormalizationState.APPLYING)}
                           endIcon={<ChevronRight/>}>
            Apply results
        </Button>
    }

    const kpiValues = React.useMemo<KpiBoxedValue[]>(() => [
        {
            title: isMaster ? 'Total Suppliers' : 'Suppliers reviewed',
            subtitle: numberOfSuppliers
                ? numberOfSuppliers
                : 'N/A'
        }, {
            title: 'Parent suppliers',
            subtitle: supplierNormalizationStore.statistics
                ? supplierNormalizationStore.statistics.parent_suppliers
                : 'N/A',
            help: <SupplierNormalizationPSHelp/>
        }].concat(hideStandaloneOverview ? [] : [
        {
            title: 'Standalone suppliers',
            subtitle: supplierNormalizationStore.statistics
                ? supplierNormalizationStore.statistics.standalone_suppliers
                : 'N/A',
            help: <SupplierNormalizationSSHelp/>
        },
    ]), [isMaster, supplierNormalizationStore.statistics, numberOfSuppliers, hideStandaloneOverview]);

    const title = (!p.p.masterCompanyName && !p.p.compareCompanyName)
        ? CARD_CONFIG['supplier-normalization'].title
        : (supplierNormalizationStore.isMaster
            ? <Typography variant="h2" component="h1">
                Supplier Normalization for {p.p.masterCompanyName + ' '}
                {p.p.showResultPills && <Chip label="SAP results" size="medium"/>}
            </Typography>
            : <Typography variant="h2" component="h1">
                Supplier Normalization for {p.p.compareCompanyName + ' '}
                {p.p.showResultPills && <Chip color="primary" label="Mithra results" size="medium"/>}
            </Typography>
        )
    return (
        <MainPageFacade
            title={title}
            onBack={goBack}
            backLabel={backLabel}
            backClass={""}
            next={{type: 'component', node: nextNode}}
            nextClass={"text-right"}
            className="sup-norm-page">

            <KpiBoxedValues values={kpiValues}/>

            {authStore.isMithraStaffGroup && <SupplierNormalizationWorkflows/>}

            {environment.isDemo ? <SupplierNormalizationStepper/> : null}

            {state === SupplierNormalizationState.START && <SupplierNormalizationWelcomeStep/>}
            {state === SupplierNormalizationState.RUNNING && <SupplierNormalizationModelRunningStep/>}
            {state === SupplierNormalizationState.REVIEW && <ParentSuppliersStep/>}
            {state === SupplierNormalizationState.APPLYING && <SupplierNormalizationApplyingResultsStep/>}
            {state === SupplierNormalizationState.DONE && <SupplierNormalizationApplyStep/>}
        </MainPageFacade>
    );
});
